import { type ApolloError, useQuery } from '@apollo/client';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAuth } from '@/auth/useAuth';
import { ROLES } from '@/constants/roles';
import { MY_COMPANY_REGISTRATION_QUERY } from '@/features/company-registration/graphql/queries';
import type { CompanyRegistration } from '@/features/company-registration/types/companyRegistration.types';

import { MY_COMPANY_MEMBERSHIPS_QUERY } from '../graphql/queries';
import type { CompanyMembership } from '../types/companyPortal.types';
import {
  type LastWorkspace,
  readLastWorkspace,
  writeLastWorkspace,
} from './workspaceStorage';

export interface WorkspaceContextValue {
  /** True until memberships + registration have loaded (only while authenticated). */
  loading: boolean;
  error: ApolloError | null;
  /** Every membership the account holds, any status. */
  memberships: CompanyMembership[];
  /** Memberships that actually grant portal access (status ACTIVE). */
  activeMemberships: CompanyMembership[];
  /** The account's latest company registration (the founder journey), if any. */
  registration: CompanyRegistration | null;
  /** Candidate workspace availability — the platform `Candidate` role. */
  hasCandidateWorkspace: boolean;
  /**
   * Company Portal access: an ACTIVE membership OR a registration in flight. A pending founder has
   * no membership yet, and must still reach their approval/bootstrap experience — never a 403.
   * A Completed registration whose bootstrapped Company was later deleted does not count: the
   * registration is a permanent, never-mutated audit record, so `registration !== null` alone cannot be
   * trusted once the Company it produced may be gone. See `registration.companyStillExists`.
   */
  hasCompanyAccess: boolean;
  /** The company currently being acted for (null while pending registration only). */
  activeCompanyId: string | null;
  activeMembership: CompanyMembership | null;
  setActiveCompany: (companyId: string) => void;
  /** Persist which workspace the user chose so the next sign-in resumes there. */
  rememberWorkspace: (workspace: LastWorkspace) => void;
  lastWorkspace: LastWorkspace | null;
  refetch: () => Promise<unknown>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined);

/**
 * Workspace membership state for the signed-in account (Company module Stage 2). One provider for
 * the whole app: `TopNav`'s workspace switcher, `RootRedirect`, the Company Portal guard and every
 * portal page read the same two cached queries (`myCompanyMemberships` + `myCompanyRegistration`)
 * instead of re-fetching per screen. Queries are skipped entirely while signed out.
 *
 * Workspace switching is NOT account switching — it only changes which business context the same
 * authenticated session is viewing; tokens and identity are untouched.
 */
/** Shortest gap between foreground re-reads, so re-clicking the window is not a request per click. */
const WorkspaceRevalidateThrottleMs = 5_000;

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();

  const membershipsQuery = useQuery(MY_COMPANY_MEMBERSHIPS_QUERY, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  });
  const registrationQuery = useQuery(MY_COMPANY_REGISTRATION_QUERY, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  });

  const memberships = useMemo(
    () => membershipsQuery.data?.myCompanyMemberships ?? [],
    [membershipsQuery.data],
  );
  const activeMemberships = useMemo(
    () => memberships.filter((m) => m.status === 'ACTIVE'),
    [memberships],
  );
  const registration = registrationQuery.data?.myCompanyRegistration ?? null;

  const lastWorkspace = useMemo(
    () => (user ? readLastWorkspace(user.id) : null),
    [user],
  );

  // The active company: an explicit user choice wins, then the remembered one, then the first
  // ACTIVE membership. Choices that no longer resolve to an ACTIVE membership are discarded.
  const [chosenCompanyId, setChosenCompanyId] = useState<string | null>(null);

  // A signed-out → signed-in transition (or account switch) must never carry the previous
  // account's company choice.
  useEffect(() => {
    setChosenCompanyId(null);
  }, [user?.id]);

  const activeCompanyId = useMemo(() => {
    const isActive = (id: string | null | undefined) =>
      Boolean(id && activeMemberships.some((m) => m.companyId === id));
    if (isActive(chosenCompanyId)) return chosenCompanyId;
    if (lastWorkspace?.kind === 'company' && isActive(lastWorkspace.companyId)) {
      return lastWorkspace.companyId;
    }
    return activeMemberships[0]?.companyId ?? null;
  }, [chosenCompanyId, lastWorkspace, activeMemberships]);

  const activeMembership = useMemo(
    () => activeMemberships.find((m) => m.companyId === activeCompanyId) ?? null,
    [activeMemberships, activeCompanyId],
  );

  const rememberWorkspace = useCallback(
    (workspace: LastWorkspace) => {
      if (user) writeLastWorkspace(user.id, workspace);
    },
    [user],
  );

  const setActiveCompany = useCallback(
    (companyId: string) => {
      setChosenCompanyId(companyId);
      if (user) writeLastWorkspace(user.id, { kind: 'company', companyId });
    },
    [user],
  );

  const refetch = useCallback(
    () => Promise.all([membershipsQuery.refetch(), registrationQuery.refetch()]),
    [membershipsQuery, registrationQuery],
  );

  // Both queries are cache-and-network, which re-reads on MOUNT — and this provider mounts once per
  // full page load, so within an SPA session nothing ever re-asks. A company archived or restored
  // elsewhere (a platform admin, or this user's own other tab) therefore sat stale in the workspace
  // switcher until a reload. Re-read when the tab returns to the foreground, which is exactly when
  // the user is about to act on what it shows.
  //
  // Held in a ref because Apollo hands back new query objects every render, so `refetch`'s identity
  // changes constantly; depending on it directly would re-subscribe these listeners on every render.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!isAuthenticated) return;

    let lastRevalidatedAt = 0;
    const revalidate = () => {
      // `focus` and `visibilitychange` both fire on a tab switch, and `focus` alone fires whenever
      // the window is re-clicked; one re-read per return is enough.
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastRevalidatedAt < WorkspaceRevalidateThrottleMs) return;
      lastRevalidatedAt = now;
      void refetchRef.current();
    };

    window.addEventListener('focus', revalidate);
    document.addEventListener('visibilitychange', revalidate);
    return () => {
      window.removeEventListener('focus', revalidate);
      document.removeEventListener('visibilitychange', revalidate);
    };
  }, [isAuthenticated]);

  const loading =
    isAuthenticated &&
    ((membershipsQuery.loading && !membershipsQuery.data) ||
      (registrationQuery.loading && !registrationQuery.data));

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      loading,
      error: membershipsQuery.error ?? registrationQuery.error ?? null,
      memberships,
      activeMemberships,
      registration,
      hasCandidateWorkspace: Boolean(user?.roles.includes(ROLES.Candidate)),
      hasCompanyAccess:
        activeMemberships.length > 0 ||
        (registration !== null && registration.companyStillExists !== false),
      activeCompanyId,
      activeMembership,
      setActiveCompany,
      rememberWorkspace,
      lastWorkspace,
      refetch,
    }),
    [
      loading,
      membershipsQuery.error,
      registrationQuery.error,
      memberships,
      activeMemberships,
      registration,
      user,
      activeCompanyId,
      activeMembership,
      setActiveCompany,
      rememberWorkspace,
      lastWorkspace,
      refetch,
    ],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

/** Inert fallback so shared chrome (e.g. `TopNav` in isolated tests) renders without the provider. */
const EMPTY_WORKSPACE: WorkspaceContextValue = {
  loading: false,
  error: null,
  memberships: [],
  activeMemberships: [],
  registration: null,
  hasCandidateWorkspace: false,
  hasCompanyAccess: false,
  activeCompanyId: null,
  activeMembership: null,
  setActiveCompany: () => undefined,
  rememberWorkspace: () => undefined,
  lastWorkspace: null,
  refetch: () => Promise.resolve(),
};

// eslint-disable-next-line react-refresh/only-export-components
export function useWorkspace(): WorkspaceContextValue {
  return useContext(WorkspaceContext) ?? EMPTY_WORKSPACE;
}
